import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Globe, Plus, Trash2, Copy, RefreshCw, CheckCircle2, Clock, AlertTriangle, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS = {
  active: { label: "Active (HTTPS)", cls: "text-emerald-400 bg-emerald-500/10", icon: CheckCircle2 },
  active_no_ssl: { label: "Live (no SSL)", cls: "text-amber-400 bg-amber-500/10", icon: AlertTriangle },
  pending: { label: "Pending DNS", cls: "text-slate-400 bg-slate-700/50", icon: Clock },
  failed: { label: "Failed", cls: "text-red-400 bg-red-500/10", icon: AlertTriangle },
};

const partialMsg = (result) => {
  const fips = result?.foreign_ips || [];
  if (fips.length > 0) {
    return `Live on HTTP, but SSL failed: your domain also points to ${fips.join(", ")} (not ours). Remove those A records so it points ONLY to us, then retry.`;
  }
  return "Live on HTTP, but SSL failed — make sure the domain points ONLY to our IP and port 80 is reachable, then retry.";
};

export const CustomDomainsPanel = ({ token }) => {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [target, setTarget] = useState(null);
  const [domains, setDomains] = useState([]);
  const [newDomain, setNewDomain] = useState("");
  const [adding, setAdding] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [autoCheck, setAutoCheck] = useState(true);
  const runningRef = useRef(false);

  const load = useCallback(async () => {
    try {
      const [t, d] = await Promise.all([
        axios.get(`${API}/user/custom-domains/target`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/user/custom-domains`, { headers }),
      ]);
      setTarget(t.data);
      setDomains(d.data.domains || []);
    } catch (e) {
      // silently ignore (e.g. unlicensed)
    }
  }, [headers]);
  useEffect(() => { load(); }, [load]);

  const limit = target?.domains_limit ?? 2;
  const full = domains.length >= limit;

  const addDomain = async () => {
    if (!newDomain.trim()) return;
    setAdding(true);
    try {
      await axios.post(`${API}/user/custom-domains`, { domain: newDomain.trim() }, { headers });
      toast.success("Domain added — point your DNS, then Verify & Activate");
      setNewDomain("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add domain");
    } finally {
      setAdding(false);
    }
  };

  const pollJob = useCallback(async (jobId) => {
    for (let i = 0; i < 40; i++) {
      await new Promise((r) => setTimeout(r, 3000));
      try {
        const { data } = await axios.get(`${API}/user/custom-domains/jobs/${jobId}`, { headers });
        if (data.status && data.status !== "running") return data;
      } catch (e) { /* keep polling */ }
    }
    return null;
  }, [headers]);

  const verifyActivate = async (id) => {
    setBusyId(id);
    try {
      const { data } = await axios.post(`${API}/user/custom-domains/${id}/verify-activate`, {}, { headers });
      if (!data.success) {
        toast.error(data.message || "DNS not pointing to us yet");
        return;
      }
      toast.info("DNS verified — provisioning SSL, this can take a minute...");
      const result = await pollJob(data.job_id);
      if (result?.status === "success") toast.success("Your domain is live with HTTPS! 🎉");
      else if (result?.status === "partial") toast.warning(partialMsg(result), { duration: 10000 });
      else toast.error("Activation failed — see status. You can retry.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Activation error");
    } finally {
      setBusyId(null);
    }
  };

  // Silent background DNS check: auto-activates pending/failed domains once their
  // A record points to us, so the user doesn't have to keep clicking.
  const silentAttempt = useCallback(async () => {
    if (runningRef.current || busyId) return;
    const targets = domains.filter((d) => ["pending", "failed", "active_no_ssl"].includes(d.status));
    if (targets.length === 0) return;
    runningRef.current = true;
    try {
      for (const d of targets) {
        try {
          const { data } = await axios.post(`${API}/user/custom-domains/${d.id}/verify-activate`, {}, { headers });
          if (data.success) {
            toast.info(`DNS detected for ${d.domain} — provisioning SSL...`);
            setBusyId(d.id);
            const result = await pollJob(data.job_id);
            if (result?.status === "success") toast.success(`${d.domain} is live with HTTPS! 🎉`);
            else if (result?.status === "partial") toast.warning(partialMsg(result), { duration: 10000 });
            setBusyId(null);
            await load();
            break; // one activation per cycle
          }
        } catch (e) { /* silent — DNS likely not ready yet */ }
      }
    } finally {
      runningRef.current = false;
    }
  }, [busyId, domains, headers, load, pollJob]);

  useEffect(() => {
    if (!autoCheck) return;
    const hasPending = domains.some((d) => ["pending", "failed", "active_no_ssl"].includes(d.status));
    if (!hasPending) return;
    const id = setInterval(silentAttempt, 30000);
    return () => clearInterval(id);
  }, [autoCheck, domains, silentAttempt]);


  const remove = async (id) => {
    if (!window.confirm("Remove this custom domain? Links using it will stop working.")) return;
    try {
      await axios.delete(`${API}/user/custom-domains/${id}`, { headers });
      toast.success("Domain removed");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not remove");
    }
  };

  return (
    <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6" data-testid="custom-domains-panel">
      <div className="flex items-center gap-3 mb-2">
        <Globe className="w-5 h-5 text-cyan-400" />
        <h3 className="text-lg font-semibold text-white">Your Own Domains</h3>
        <span className="text-xs text-slate-400">({domains.length}/{limit})</span>
      </div>
      <p className="text-sm text-slate-400 mb-4">
        Use your own branded domain (e.g. <span className="text-slate-300">go.yourbrand.com</span>) for redirects.
        In your domain's <b className="text-slate-300">DNS settings</b> (or the <b className="text-slate-300">Zone Editor</b> in cPanel),
        add an <b className="text-slate-300">A record</b> pointing to <b className="text-slate-300">any one</b> of our server IPs below, then click Verify &amp; Activate — we detect which server it points to and set up SSL automatically.
      </p>

      {Array.isArray(target?.servers) && target.servers.length > 0 && (
        <div className="mb-4 p-3 rounded-lg bg-slate-900/60 border border-slate-700">
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck className="w-4 h-4 text-cyan-400 shrink-0" />
            <span className="text-sm text-slate-300">Point an <b>A record</b> to any one of our server IPs:</span>
          </div>
          <div className="space-y-1.5 pl-6" data-testid="byod-server-ip-list">
            {target.servers.map((s, i) => {
              const online = s.is_online !== false;
              return (
                <div key={s.ip_address} className={`flex items-center gap-2 ${online ? "" : "opacity-45"}`} data-testid={`byod-server-${i}`}>
                  <code className={`text-sm font-mono ${online ? "text-cyan-300" : "text-slate-400 line-through"}`} data-testid={`target-ip-${i}`}>{s.ip_address}</code>
                  {online && (
                    <button
                      onClick={() => { navigator.clipboard.writeText(s.ip_address); toast.success("IP copied"); }}
                      className="p-1 text-slate-400 hover:text-white" title="Copy IP" data-testid={`copy-target-ip-${i}`}
                    ><Copy className="w-3.5 h-3.5" /></button>
                  )}
                  <span className="text-xs text-slate-500">{s.server_name}</span>
                  {online ? (
                    s.ip_address === target.ip_address && (
                      <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-cyan-500/15 text-cyan-300 border border-cyan-500/30">recommended</span>
                    )
                  ) : (
                    <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-slate-600/20 text-slate-400 border border-slate-600/40">offline — don't use</span>
                  )}
                </div>
              );
            })}
          </div>
          <div className="mt-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs text-slate-400 pl-6">
            <span className="text-slate-500">Type:</span><span className="text-slate-300 font-mono">A</span>
            <span className="text-slate-500">Host / Name:</span><span className="text-slate-300 font-mono">your subdomain (e.g. <b>go</b>) or <b>@</b> for the root domain</span>
            <span className="text-slate-500">Value / Points to:</span><span className="text-slate-300 font-mono">any IP above</span>
            <span className="text-slate-500">TTL:</span><span className="text-slate-300 font-mono">Automatic (or 300)</span>
          </div>
          <p className="text-xs text-slate-500 mt-2 pl-6">DNS can take a few minutes (up to an hour) to propagate before Verify &amp; Activate succeeds.</p>
        </div>
      )}

      <div className="flex gap-2 mb-4">
        <input
          value={newDomain}
          onChange={(e) => setNewDomain(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && !full && addDomain()}
          placeholder="go.yourbrand.com"
          disabled={full}
          className="flex-1 px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-cyan-500 disabled:opacity-50"
          data-testid="custom-domain-input"
        />
        <button
          onClick={addDomain}
          disabled={adding || full || !newDomain.trim()}
          className="flex items-center gap-1.5 px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white text-sm rounded-lg transition-colors disabled:opacity-40"
          data-testid="add-custom-domain-btn"
        >
          {adding ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add
        </button>
      </div>
      {full && <p className="text-xs text-amber-400 mb-3">You've reached your limit of {limit} custom domains.</p>}

      {domains.some((d) => ["pending", "failed", "active_no_ssl"].includes(d.status)) && (
        <div className="flex items-center justify-between gap-3 mb-3 p-2.5 rounded-lg bg-cyan-500/5 border border-cyan-500/20" data-testid="byod-autocheck-hint">
          <span className="flex items-center gap-2 text-xs text-cyan-300">
            <RefreshCw className={`w-3.5 h-3.5 shrink-0 ${autoCheck ? "animate-spin" : ""}`} />
            {autoCheck
              ? "Auto-checking your DNS every 30s — activation & SSL happen automatically once it points to us. You can leave this page open."
              : "Auto-check is off. Click Verify & Activate once your DNS points to us."}
          </span>
          <button
            onClick={() => setAutoCheck((v) => !v)}
            className="text-xs px-2.5 py-1 rounded border border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/10 shrink-0"
            data-testid="byod-autocheck-toggle"
          >
            {autoCheck ? "Turn off" : "Turn on"}
          </button>
        </div>
      )}

      <div className="space-y-2">
        {domains.length === 0 && <p className="text-sm text-slate-500">No custom domains yet.</p>}
        {domains.map((d) => {
          const st = STATUS[d.status] || STATUS.pending;
          const Icon = st.icon;
          return (
            <div key={d.id} className="flex items-center gap-3 p-3 rounded-lg bg-slate-900/40 border border-slate-700" data-testid={`custom-domain-${d.id}`}>
              <span className="text-white text-sm font-medium truncate">{d.domain}</span>
              <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${st.cls}`} data-testid={`custom-domain-status-${d.id}`}>
                <Icon className="w-3 h-3" /> {st.label}
              </span>
              <div className="ml-auto flex items-center gap-2">
                {(d.status === "pending" || d.status === "failed" || d.status === "active_no_ssl") && (
                  <button
                    onClick={() => verifyActivate(d.id)}
                    disabled={busyId === d.id}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg transition-colors disabled:opacity-50"
                    data-testid={`verify-activate-${d.id}`}
                  >
                    {busyId === d.id ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                    Verify & Activate
                  </button>
                )}
                <button onClick={() => remove(d.id)} className="p-1.5 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg" title="Remove" data-testid={`remove-custom-domain-${d.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
